// Rolls a hoard of loot for an encounter from a campaign's loot tables.
//
// Entries only count if they fit the encounter: its difficulty, the highest CR of the creatures in it,
// its Environments, and its creatures. Guaranteed entries that fit are each given once,
// and then each roll picks one of the other entries that fit, by weight.
// An entry gives its money and each of its item groups, which can be items picked at random
// from a limitation, a specific item, or rolls on another loot table (picked by weight alone).
//
// Matching creatures, finding items, and finding other loot tables need the api, so those are passed in,
// and everything else works on plain data so it can be tested.

import { EncounterDifficulty } from "./encounter_generator";

export const LOOT_ROLLS_BY_DIFFICULTY: { [difficulty in EncounterDifficulty]: number } = {
  Easy: 1,
  Medium: 2,
  Hard: 3,
  Deadly: 4,
};

export interface DiceLike {
  size: number; // 1 means count is a flat number
  count: number;
}

export interface ItemGroupLike<I> {
  source: "Items" | "Specific Item" | "Loot Table";
  count: DiceLike;
  item_limit: I;
  min_value: number;
  max_value: number;
  item_id: string;
  loot_table_id: string;
}

export interface LootEntryLike<C, I> {
  true_id: string;
  name: string;
  weight: number;
  guaranteed: boolean;
  environment_ids: string[];
  difficulties: string[];
  min_cr: number;
  max_cr: number;
  creature_limit: C;
  item_groups: ItemGroupLike<I>[];
  money: { pp: DiceLike, gp: DiceLike, ep: DiceLike, sp: DiceLike, cp: DiceLike };
}

export interface LootLookups<C, I> {
  // The items an Items or Specific Item group can give (Items groups already filtered by value)
  itemsFor: (group: ItemGroupLike<I>) => { id: string, name: string }[];
  // Another loot table, for Loot Table groups
  subTable: (loot_table_id: string) => { table_name: string, entries: LootEntryLike<C, I>[] } | undefined;
}

export interface LootSource<C, I> {
  table_id: string;
  table_name: string;
  entries: LootEntryLike<C, I>[];
}

export interface LootContext<C> {
  difficulty: EncounterDifficulty;
  environment_ids: string[];
  known_environment_ids: string[]; // All Environments that exist, so leftover values can be ignored
  max_cr: number; // Highest CR of the creatures in the encounter
  creatureMatches: (limit: C) => boolean; // Whether any creature in the encounter fits (true for an empty limit)
}

export interface LootCandidate<C, I> {
  table_id: string;
  table_name: string;
  entry: LootEntryLike<C, I>;
}

export interface RolledItem {
  id: string; // PseudoItem id
  name: string;
  count: number;
}

export interface RolledMoney {
  pp: number;
  gp: number;
  ep: number;
  sp: number;
  cp: number;
}

export interface RolledLoot {
  key: string;
  table_id: string;
  table_name: string;
  entry_id: string;
  entry_name: string;
  guaranteed: boolean;
  items: RolledItem[];
  money: RolledMoney;
  sub_rolls: string[]; // What was picked on other loot tables, like "Magic Items A: Potion of Healing"
  problems: string[]; // Things that couldn't be given, like items nothing fits
}

// Rolls on other loot tables can nest, but not forever (a table could include itself).
const MAX_SUB_TABLE_DEPTH = 5;

export const rollDice = (dice: DiceLike, rng: () => number): number => {
  if (dice.size === 1) {
    return dice.count;
  }
  const count = Math.abs(dice.count);
  let total = 0;
  for (let i = 0; i < count; ++i) {
    total += Math.floor(rng() * dice.size) + 1;
  }
  return dice.count < 0 ? -total : total;
};

export const entryFits = <C, I>(entry: LootEntryLike<C, I>, ctx: LootContext<C>): boolean => {
  if (entry.weight <= 0) {
    return false;
  }
  if (entry.difficulties.length > 0 && !entry.difficulties.includes(ctx.difficulty)) {
    return false;
  }
  if (entry.min_cr >= 0 && ctx.max_cr < entry.min_cr) {
    return false;
  }
  if (entry.max_cr >= 0 && ctx.max_cr > entry.max_cr) {
    return false;
  }
  if (ctx.environment_ids.length > 0) {
    // Entries without any Environments fit anywhere
    const known = entry.environment_ids.filter(o => ctx.known_environment_ids.includes(o));
    if (known.length > 0 && !known.some(o => ctx.environment_ids.includes(o))) {
      return false;
    }
  }
  return ctx.creatureMatches(entry.creature_limit);
};

export const fittingEntries = <C, I>(sources: LootSource<C, I>[], ctx: LootContext<C>): LootCandidate<C, I>[] => {
  const candidates: LootCandidate<C, I>[] = [];
  for (const source of sources) {
    for (const entry of source.entries) {
      if (entryFits(entry, ctx)) {
        candidates.push({ table_id: source.table_id, table_name: source.table_name, entry });
      }
    }
  }
  return candidates;
};

export const pickWeighted = <T extends { entry: { weight: number } }>(candidates: T[], rng: () => number): T | undefined => {
  const total = candidates.reduce((sum, o) => sum + o.entry.weight, 0);
  let roll = rng() * total;
  for (const candidate of candidates) {
    roll -= candidate.entry.weight;
    if (roll < 0) {
      return candidate;
    }
  }
  return candidates[candidates.length - 1];
};

let next_key = 0;

// Adds what an entry gives to a result, following rolls on other loot tables.
const giveEntry = <C, I>(
  entry: LootEntryLike<C, I>,
  result: RolledLoot,
  lookups: LootLookups<C, I>,
  rng: () => number,
  depth: number
) => {
  const label = entry.name === "" ? "(Unnamed Entry)" : entry.name;
  for (const group of entry.item_groups) {
    const count = Math.max(rollDice(group.count, rng), 0);
    if (count === 0) {
      continue;
    }
    if (group.source === "Loot Table") {
      const sub = lookups.subTable(group.loot_table_id);
      const sub_entries = sub ? sub.entries.filter(o => o.weight > 0) : [];
      if (!sub) {
        result.problems.push(`${label} rolls on a loot table that couldn't be found.`);
      } else if (sub_entries.length === 0) {
        result.problems.push(`${label} rolls on ${sub.table_name}, which has no entries.`);
      } else if (depth >= MAX_SUB_TABLE_DEPTH) {
        result.problems.push(`${label} rolls on ${sub.table_name} too many tables deep, so it was skipped.`);
      } else {
        for (let i = 0; i < count; ++i) {
          const picked = pickWeighted(sub_entries.map(o => ({ entry: o })), rng);
          if (picked) {
            result.sub_rolls.push(`${sub.table_name}: ${picked.entry.name === "" ? "(Unnamed Entry)" : picked.entry.name}`);
            giveEntry(picked.entry, result, lookups, rng, depth + 1);
          }
        }
      }
    } else {
      const options = lookups.itemsFor(group);
      if (options.length === 0) {
        result.problems.push(`${count} item(s) from ${label} couldn't be found, since no items fit it with these source books.`);
      } else {
        for (let i = 0; i < count; ++i) {
          const option = options[Math.floor(rng() * options.length)];
          const existing = result.items.find(o => o.id === option.id);
          if (existing) {
            existing.count++;
          } else {
            result.items.push({ id: option.id, name: option.name, count: 1 });
          }
        }
      }
    }
  }
  const money = entry.money;
  result.money.pp += Math.max(rollDice(money.pp, rng), 0);
  result.money.gp += Math.max(rollDice(money.gp, rng), 0);
  result.money.ep += Math.max(rollDice(money.ep, rng), 0);
  result.money.sp += Math.max(rollDice(money.sp, rng), 0);
  result.money.cp += Math.max(rollDice(money.cp, rng), 0);
};

export const rollEntry = <C, I>(
  candidate: LootCandidate<C, I>,
  lookups: LootLookups<C, I>,
  rng: () => number
): RolledLoot => {
  const result: RolledLoot = {
    key: `loot_${next_key++}`,
    table_id: candidate.table_id,
    table_name: candidate.table_name,
    entry_id: candidate.entry.true_id,
    entry_name: candidate.entry.name,
    guaranteed: candidate.entry.guaranteed,
    items: [],
    money: { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 },
    sub_rolls: [],
    problems: [],
  };
  giveEntry(candidate.entry, result, lookups, rng, 0);
  return result;
};

// Every guaranteed entry that fits once, then rolls times on the rest.
export const rollLoot = <C, I>({
  candidates,
  rolls,
  lookups,
  rng = Math.random,
}: {
  candidates: LootCandidate<C, I>[];
  rolls: number;
  lookups: LootLookups<C, I>;
  rng?: () => number;
}): RolledLoot[] => {
  const results: RolledLoot[] = candidates.filter(o => o.entry.guaranteed).map(o => rollEntry(o, lookups, rng));
  const weighted = candidates.filter(o => !o.entry.guaranteed);
  if (weighted.length > 0) {
    for (let i = 0; i < rolls; ++i) {
      const candidate = pickWeighted(weighted, rng);
      if (candidate) {
        results.push(rollEntry(candidate, lookups, rng));
      }
    }
  }
  return results;
};

// A new result to replace one. Guaranteed ones roll the same entry again, the rest pick a new entry.
export const rerollLoot = <C, I>({
  result,
  candidates,
  lookups,
  rng = Math.random,
}: {
  result: RolledLoot;
  candidates: LootCandidate<C, I>[];
  lookups: LootLookups<C, I>;
  rng?: () => number;
}): RolledLoot | undefined => {
  if (result.guaranteed) {
    const candidate = candidates.find(o => o.table_id === result.table_id && o.entry.true_id === result.entry_id);
    return candidate ? rollEntry(candidate, lookups, rng) : undefined;
  }
  const candidate = pickWeighted(candidates.filter(o => !o.entry.guaranteed), rng);
  return candidate ? rollEntry(candidate, lookups, rng) : undefined;
};

// Adds up all the money in a set of results.
export const totalMoney = (results: RolledLoot[]): RolledMoney => {
  return results.reduce((total, o) => ({
    pp: total.pp + o.money.pp,
    gp: total.gp + o.money.gp,
    ep: total.ep + o.money.ep,
    sp: total.sp + o.money.sp,
    cp: total.cp + o.money.cp,
  }), { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 });
};

export const moneyString = (money: RolledMoney): string => {
  const parts: string[] = [];
  if (money.pp > 0) parts.push(`${money.pp} pp`);
  if (money.gp > 0) parts.push(`${money.gp} gp`);
  if (money.ep > 0) parts.push(`${money.ep} ep`);
  if (money.sp > 0) parts.push(`${money.sp} sp`);
  if (money.cp > 0) parts.push(`${money.cp} cp`);
  return parts.join(", ");
};
